import { useState } from "react";
import { motion } from "framer-motion";
import { X, Trophy } from "lucide-react";
import ConfettiBurst from "@/components/ConfettiBurst";
import LatteArt from "@/components/minigames/LatteArt";
import SpellCasting from "@/components/minigames/SpellCasting";
import ClueHunt from "@/components/minigames/ClueHunt";
import { WORLD_EXTRAS } from "@/data";
import { sound } from "@/lib/sound";

const GAMES = { cafe: LatteArt, academy: SpellCasting, alley: ClueHunt };

export default function MiniGame({ world, onClose, onComplete }) {
  const [won, setWon] = useState(false);
  const Game = GAMES[world.id];
  const meta = WORLD_EXTRAS[world.id].minigame;

  const handleWin = () => {
    if (won) return;
    setWon(true);
    sound.playSuccess();
    sound.playBurst(world.id);
    onComplete(world.id);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[115] flex items-center justify-center p-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="minigame-overlay"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative w-full max-w-lg overflow-hidden rounded-3xl border bg-black/85 p-7 backdrop-blur-2xl"
        style={{ borderColor: `${world.accent}55`, boxShadow: `0 0 50px ${world.accentSoft}` }}
        initial={{ scale: 0.92, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1" style={{ background: world.accent }} />
        <button
          onClick={onClose}
          data-testid="minigame-close"
          className="absolute right-4 top-4 z-10 rounded-full p-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="mb-1 text-xs uppercase tracking-[0.25em]" style={{ color: world.accent }}>
          {meta.name}
        </div>
        <div className="kr mb-5 text-sm text-white/45">{meta.nameKr}</div>

        {!won ? (
          <Game world={world} onWin={handleWin} />
        ) : (
          <div className="py-6 text-center" data-testid="minigame-success">
            <Trophy className="mx-auto h-10 w-10" style={{ color: world.accent }} />
            <p className="mt-4 text-base leading-relaxed text-white/85">{meta.reward}</p>
            <button
              onClick={onClose}
              className="mt-6 rounded-full px-6 py-2.5 text-sm font-medium text-black transition-transform hover:scale-105"
              style={{ background: world.accent }}
            >
              Continue
            </button>
          </div>
        )}
      </motion.div>
      {won && <ConfettiBurst color={world.accent} />}
    </motion.div>
  );
}
